import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { DataMap } from '@grapecity/wijmo.grid'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, GridPanel } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FabHold,
    FabHoldReleaseRequest,
    FabHoldsView,
    FabJob,
    FabPiece,
    FabProcessStep,
    FeatureType,
    Grid,
    ListItem,
    replaceListItemsIfChanged,
    ModalSize,
    User,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { FabHoldComponent, FabHoldModalParent } from './fab-hold.component'

@Component({
    selector: 'app-fab-holds',
    templateUrl: './fab-holds.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class FabHoldsComponent extends BaseComponent implements FabHoldModalParent {
    private route = inject(ActivatedRoute)
    private router = inject(Router)
    private destroyRef = inject(DestroyRef)

    grid: Grid<FabHold>
    jobs: FabJob[] = []
    jobOptions: ListItem<string>[] = []
    selectedJobId = ''
    private appliedJobId = ''
    includeReleased = false
    openCount = 0
    overdueReleaseCount = 0
    pieceOptions: ListItem<string>[] = []
    reasonOptions: ListItem<string>[] = []
    stepOptions: ListItem<string>[] = []
    userList: ListItem<string>[] = []
    userMap: DataMap | null = null

    private pendingHoldId: string | null = null
    private lookupsLoaded = false
    private holdsLoaded = false
    private expandedHoldSearch = false
    private ignoreFilterEvents = false

    constructor() {
        super()
        this.pendingHoldId = this.route.snapshot.queryParamMap.get('holdId')
        this.grid = new Grid<FabHold>(this.services, 'id', this)
            .setupModalEditing(this.services.data.fabHolds, 'Hold', FabHoldComponent, () => this.newHold(), this, ModalSize.large)
            .withColumnFilters()
            .filterBy(item => [
                item.reasonName,
                item.pieceName,
                item.markCode,
                item.scope,
                item.jobDisplayName,
                item.processStepName,
                item.description,
            ])
            .isReadOnly(true)

        this.grid.itemAdded.on(() => this.refresh())
        this.grid.itemEdited.on(() => this.refresh())
        this.grid.itemsDeleted.on(() => this.refresh())

        this.loadLookups()
        this.refresh()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FabHoldsEdit])
    }

    newHold(): FabHold {
        const hold = new FabHold()
        hold.id = crypto.randomUUID()
        hold.startedOn = new Date()
        hold.isOpen = true
        hold.daysHeld = 0
        return hold
    }

    onJobChanged() {
        if (this.ignoreFilterEvents) return
        if (this.selectedJobId === this.appliedJobId) return
        this.refresh(false)
    }

    onIncludeReleasedChanged() {
        if (this.ignoreFilterEvents) return
        this.refresh(false)
    }

    releaseSelected() {
        const hold = this.grid.selectedItem as FabHold
        if (!hold || !hold.isOpen) {
            this.services.notification.error('Select an open hold to release.')
            return
        }

        this.services.modals
            .confirm('Release Hold', `Release hold on ${hold.markCode || hold.pieceName || 'this piece'}?`)
            .then(() => {
                const body: FabHoldReleaseRequest = {
                    fabHoldId: hold.id,
                    releasedOn: new Date(),
                    releaseNote: null,
                }
                this.services.data.post('api/FabHold/Release', body).subscribe({
                    next: () => {
                        this.services.notification.success('Hold released.')
                        this.refresh()
                    },
                    error: () => this.services.notification.error('Failed to release hold.'),
                })
            })
            .catch(() => undefined)
    }

    refresh(showSpinner = true) {
        const requestedJobId = this.selectedJobId
        let params = new HttpParams().set('includeReleased', String(this.includeReleased))
        if (requestedJobId) {
            params = params.set('fabJobId', requestedJobId)
        }

        this.cancelableRequest(
            '',
            showSpinner,
            this.services.data.load(showSpinner, showSpinner, this.services.data.get<FabHoldsView>('api/FabViews/Holds', params)).subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    const view = result[0]
                    this.jobs = view.jobs || []
                    this.openCount = view.openCount || 0
                    this.overdueReleaseCount = view.overdueReleaseCount || 0
                    this.jobOptions = replaceListItemsIfChanged(this.jobOptions, [
                        new ListItem('', '(All jobs)'),
                        ...this.jobs.map(j => new ListItem(j.id, j.displayName || j.jobDescription || j.name || '')),
                    ])
                    this.appliedJobId = requestedJobId
                    if (this.selectedJobId !== requestedJobId) {
                        this.selectedJobId = requestedJobId
                    }
                    this.reasonOptions = (view.reasons || [])
                        .filter(r => r.active)
                        .map(r => new ListItem(r.id, r.name))
                    this.grid.items = view.holds || []
                    this.ignoreFilterEvents = false
                    this.holdsLoaded = true
                    this.tryOpenHoldFromLink()
                },
            }),
        )
    }

    private tryOpenHoldFromLink() {
        const holdId = this.pendingHoldId
        if (!holdId || !this.lookupsLoaded || !this.holdsLoaded) return

        const row = this.grid.items.find(h => String(h.id).toLowerCase() === holdId.toLowerCase())
        if (!row) {
            if (!this.expandedHoldSearch) {
                this.expandedHoldSearch = true
                this.holdsLoaded = false
                this.ignoreFilterEvents = true
                this.selectedJobId = ''
                this.includeReleased = true
                this.refresh(false)
                return
            }

            this.pendingHoldId = null
            this.clearHoldIdParam()
            this.services.notification.warning('Hold not found')
            return
        }

        this.pendingHoldId = null
        this.grid.selectedItem = row
        this.grid.editItem(row)
        this.clearHoldIdParam()
    }

    private clearHoldIdParam() {
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { holdId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        })
    }

    private loadLookups() {
        this.services.data
            .load(
                false,
                false,
                this.services.data.fabPieces.queryAll(),
                this.services.data.fabProcessSteps.queryAll(),
                this.services.data.users.queryAllByAction('GetForLookup'),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    const pieces = result[0] as FabPiece[]
                    const steps = result[1] as FabProcessStep[]
                    const users = result[2] as User[]
                    this.pieceOptions = pieces.map(
                        p => new ListItem(p.id, `${p.markCode ? p.markCode + ' — ' : ''}${p.name}${p.scope ? ' (' + p.scope + ')' : ''}`),
                    )
                    this.stepOptions = steps
                        .slice()
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(s => new ListItem(s.id, s.name))
                    this.userList = users.filter(u => u.active).map(u => new ListItem(u.id, u.displayName))
                    this.userMap = new DataMap(users, 'id', 'displayName')
                    this.lookupsLoaded = true
                    this.tryOpenHoldFromLink()
                },
            })
    }

    formatItem = (panel: GridPanel, r: number, _c: number, cell: HTMLDivElement) => {
        if (panel.cellType !== CellType.Cell) return
        const item = panel.rows[r]?.dataItem as FabHold
        if (!item) return
        cell.classList.remove('red-cell')
        cell.style.textDecoration = 'unset'
        if (item.isOpen && item.expectedReleaseDate) {
            const expected = new Date(item.expectedReleaseDate)
            if (expected < new Date(new Date().toDateString())) {
                cell.classList.add('red-cell')
            }
        }
        if (!item.isOpen) {
            cell.style.textDecoration = 'line-through'
        }
    }
}
